/**
 * PromotionCartApi — thin wrappers around the promotion-cart Cloud Functions.
 *
 * All money math (cost, days, endAt) is recomputed server-side; client
 * estimates produced via src/constants/promotionTiers.js are advisory.
 */
import { httpsCallable } from "firebase/functions";
import { functions } from "../FirebaseConfig";

const call = (name) => httpsCallable(functions, name);

export const PromotionCartApi = {
  /**
   * @param {object} params
   * @param {"businessListing"|"event"} params.targetType
   * @param {string} params.targetId
   * @param {"boost"|"spotlight"|"featured"|"premium"|"custom"} params.promotionType
   * @param {number} [params.count=1]                 ignored when promotionType==="custom"
   * @param {string|Date} [params.startAt]            ISO string or Date (UTC)
   * @param {string|Date} [params.endAt]              required when promotionType==="custom"
   * @param {{rrule:string, until?:string|Date}} [params.recurrence]
   * @param {string} [params.notes]
   */
  async add(params) {
    const res = await call("addPromotionToCart")(serializeDates(params));
    return res.data;
  },

  async update(itemId, patch) {
    const res = await call("updatePromotionCartItem")(serializeDates({ itemId, ...patch }));
    return res.data;
  },

  async remove(itemId) {
    const res = await call("removePromotionFromCart")({ itemId });
    return res.data;
  },

  async get() {
    const res = await call("getPromotionCart")({});
    return res.data; // { items, grandTotal, tokenBalance, shortfall, canCheckout }
  },

  async checkout() {
    const res = await call("checkoutPromotionCart")({});
    return res.data; // { success, cartId, newTokenBalance, grandTotal, stacks }
  },

  async cancelStack({ targetType, targetId, stackId }) {
    const res = await call("cancelPromotionStack")({ targetType, targetId, stackId });
    return res.data;
  },
};

function serializeDates(obj) {
  if (!obj || typeof obj !== "object") return obj;
  const out = { ...obj };
  for (const k of ["startAt", "endAt"]) {
    if (out[k] instanceof Date) out[k] = out[k].toISOString();
  }
  if (out.recurrence && out.recurrence.until instanceof Date) {
    out.recurrence = { ...out.recurrence, until: out.recurrence.until.toISOString() };
  }
  return out;
}

export default PromotionCartApi;
